import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { ADMIN_MEMBERS } from "@/constants/testIds";
import { formatApiError, linkMemberSupplier, searchErpSuppliers } from "@/lib/api";

const MIN_QUERY = 3;

// The API reports a link refusal by its reason code; these are the readable
// versions. Anything else falls back to whatever the API said.
const LINK_ERRORS = {
  member_already_linked: "Unlink the current ERP Supplier first",
  supplier_already_linked: "That ERP Supplier already belongs to another member",
  supplier_not_found: "That ERP Supplier is missing or disabled in ERP",
  erp_unavailable: "ERP could not be reached, please try again",
};

// Greentek finance holds the PAN, bank details and documents in ERP. The
// picker only reports whether a PAN is on file, never the number itself.
export const SupplierLinkDialog = ({ member, open, onOpenChange, onLinked }) => {
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(null);

  const reset = (next) => {
    if (!next) {
      setTerm("");
      setRows([]);
      setSearched(false);
    }
    onOpenChange(next);
  };

  const search = async (event) => {
    event.preventDefault();
    const query = term.trim();
    if (query.length < MIN_QUERY) {
      toast.error("Enter at least three characters to search ERP");
      return;
    }
    setSearching(true);
    try {
      setRows(await searchErpSuppliers(query));
      setSearched(true);
    } catch (error) {
      toast.error(formatApiError(error, "Could not search ERP Suppliers"));
    } finally {
      setSearching(false);
    }
  };

  const link = async (supplier) => {
    setLinking(supplier);
    try {
      await linkMemberSupplier(member.id, supplier);
      toast.success(`${member.name} is linked to ${supplier}`);
      reset(false);
      await onLinked();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        LINK_ERRORS[detail] || formatApiError(error, "Could not link this ERP Supplier"),
      );
    } finally {
      setLinking(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-lg" data-testid={ADMIN_MEMBERS.supplierDialog}>
        <DialogHeader>
          <DialogTitle>Link ERP Supplier</DialogTitle>
          <DialogDescription>
            Search the Supplier record Greentek finance pays {member?.name} through.
            Search by name, Supplier id, GSTIN or mobile number.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={search} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-subtle" />
            <Input
              data-testid={ADMIN_MEMBERS.supplierSearchInput}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Supplier name, id, GSTIN or mobile"
              className="h-10 rounded-[10px] border-black/10 bg-white pl-9"
            />
          </div>
          <Button
            data-testid={ADMIN_MEMBERS.supplierSearchButton}
            type="submit"
            disabled={searching}
            className="h-10 rounded-[10px] bg-brand-forest text-white"
          >
            {searching ? <Loader2 className="animate-spin" size={16} /> : "Search"}
          </Button>
        </form>
        <div className="max-h-[320px] space-y-2 overflow-y-auto">
          {rows.map((row) => (
            <div
              key={row.name}
              data-testid={ADMIN_MEMBERS.supplierResult}
              className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-black/5 bg-brand-surface p-3"
            >
              <div className="min-w-0">
                <b className="text-[14px]">{row.supplier_name || row.name}</b>
                <div className="mt-1 text-[12px] text-brand-muted">
                  {[row.name, row.mobile_no].filter(Boolean).join(" · ")}
                </div>
                <span className="text-[11px] font-semibold text-brand">
                  {row.pan_on_file ? "PAN on file" : "No PAN"}
                </span>
              </div>
              <Button
                data-testid={`${ADMIN_MEMBERS.supplierLinkButton}-${row.name}`}
                size="sm"
                disabled={linking !== null}
                onClick={() => link(row.name)}
              >
                Link
              </Button>
            </div>
          ))}
          {searched && !rows.length && (
            <p className="empty-copy">No enabled ERP Supplier matches that search.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
